type Event_<E> = {
  [k in keyof E]:{ stage:k,data:E[k] }
}
export type Event<E> = Event_<E>[keyof E]

type State_<S> = {
  [k in keyof S]:{ stage:k,data:S[k] }
}
export type State<S> = State_<S>[keyof S]
export type StateR<S,R> = {
  [k in keyof S]:(_:S[k]) => R
}

export const stateToR = <S,R>(s:State<S>,x:StateR<S,R>) : R => {
  return x[s.stage](s.data)
}

type F<S,E,ae extends keyof E,as extends keyof S | undefined,at extends keyof S> =
  as extends undefined
  ? (_e:Event_<E>[ae]['data']) => State_<S>[at]['data']
  : (_e:Event_<E>[ae]['data'],_s:State_<S>[as & keyof S]['data']) => State_<S>[at]['data']
export type Transition<S,E> = (_a:Event<E>,_s:State<S>) => State<S> | undefined
const mkTransition3 = <S,E,ae extends keyof E,as extends keyof S | undefined,at extends keyof S>(
  ae:ae,
  as:as,
  at:at,
  f:F<S,E,ae,as,at>
) : Transition<S,E> => (a,s) => {
  if(a.stage === ae)
  {
    if(as === undefined)
    {
      return { 'stage':at,'data':(f as (_e:Event_<E>[ae]['data']) => State_<S>[at]['data'])(a.data as Event_<E>[ae]['data']) } as State_<S>[at]
    }
    else if(s.stage === as)
    {
      return { 'stage':at,'data':(f as (_e:Event_<E>[ae]['data'],_s:State_<S>[as & keyof S]['data']) => State_<S>[at]['data'])(a.data as Event_<E>[ae]['data'],s.data as State_<S>[as & keyof S]['data']) } as State_<S>[at]
    }
  }
}
const mkTransition2 = <S,E,ae extends keyof S & keyof E,as extends (keyof S & keyof E) | undefined,at extends keyof S & keyof E>(
  ae:ae,
  as:as,
  at:at,
  f:F<S,E,ae,as,at>
) : Transition<S,E> => mkTransition3<S,E,ae,as,at>(ae,as,at,f)
const mkTransition1 = <S,E,ae extends keyof S & keyof E,as extends (keyof S & keyof E) | undefined>(
  ae:ae,
  as:as,
  f:
    as extends undefined
    ? (_e:Event_<E>[ae]) => State_<S>[ae]
    : (_e:Event_<E>[ae],_s:State_<S>[as & keyof S]) => State_<S>[ae]
) : Transition<S,E> => mkTransition2<S,E,ae,as,ae>(
  ae,
  as,
  ae,
  (
    as === undefined
    ? (a:Event_<E>[ae]['data']) => (f as (_e:Event_<E>[ae]) => State_<S>[ae])({ 'stage':ae,'data':a } as Event_<E>[ae]).data
    : (a:Event_<E>[ae]['data'],s:State_<S>[as & keyof S]['data']) => (f as (_e:Event_<E>[ae],_s:State_<S>[as & keyof S]) => State_<S>[ae])({ 'stage':ae,'data':a } as Event_<E>[ae],{ 'stage':as,'data':s } as State_<S>[as & keyof S]).data
  ) as unknown as F<S,E,ae,as,ae>
)
export type MkTransition1 = typeof mkTransition1
export type MkTransition2 = typeof mkTransition2
export type MkTransition3 = typeof mkTransition3
export const makeReducer = <S,E>(xs:(_1:MkTransition1,_2:MkTransition2,_3:MkTransition3) => Transition<S,E>[]) => (state:State<S>,action:Event<E>) : State<S> => {
  for(const x of xs(mkTransition1,mkTransition2,mkTransition3))
  {
    const temp = x(action,state)
    if(temp !== undefined)
    {
      return temp
    }
  }
  throw `invalid state transition - ${String(action.stage)} ${String(state.stage)}`
}

export type Dispatch<E> = <k extends keyof E>(k:k,data:E[k]) => void
export const dispatcher = <E,>(dispatch:React.Dispatch<Event<E>>) : Dispatch<E> => <k extends keyof E>(k:k,data:E[k]) => {
  return dispatch({ stage:k,data:data } as Event<E>)
}
